<template>
  <IntercomModal
    v-model:show="showModal"
    :title="product ? $t('products.editProduct') : $t('products.addProduct')"
    width="600px"
  >
    <div class="product-form" @keydown.escape="handleEscape">
      <!-- Essentials -->
      <IntercomInput
        ref="nameRef"
        v-model="formData.name"
        :label="$t('products.name')"
        :placeholder="$t('products.name')"
        required
        :error="errors.name"
        @keydown.enter.prevent="advance('name')"
      />

      <IntercomInput
        ref="basePriceRef"
        v-model.number="formData.base_price"
        type="number"
        :label="$t('products.basePrice')"
        :placeholder="$t('products.basePrice')"
        required
        :error="errors.base_price"
        @keydown.enter.prevent="advance('basePrice')"
      />

      <!-- Product Type: search HSN catalog, auto-fills HSN + GST -->
      <div class="product-type-field">
        <label class="field-label-sm">Product Type (for GST)</label>
        <div class="hsn-search-wrapper">
          <input
            ref="hsnSearchRef"
            v-model="hsnSearchQuery"
            type="text"
            class="hsn-search-input"
            placeholder="Search... e.g. pen, rice, biscuit"
            @input="handleHSNSearch"
            @focus="handleHSNFocus"
            @blur="closeHSNDropdown"
            @keydown.escape="hsnDropdownOpen = false"
            @keydown.enter.prevent="advance('hsnSearch')"
          />
          <button
            v-if="hsnSearchQuery"
            type="button"
            class="hsn-clear-btn"
            @mousedown.prevent="clearHSNSelection"
          >x</button>
          <!-- Dropdown results -->
          <div v-if="hsnDropdownOpen && (hsnOptions.length > 0 || isSearchingHSN)" class="hsn-dropdown">
            <div v-if="isSearchingHSN" class="hsn-dropdown-loading">Searching...</div>
            <div
              v-for="option in hsnOptions"
              :key="option.value"
              class="hsn-dropdown-item"
              @mousedown.prevent="selectHSNOption(option)"
            >
              <span class="hsn-item-label">{{ option.label }}</span>
              <span class="hsn-item-meta">{{ option.hsn }} · {{ option.gst }}%</span>
            </div>
          </div>
        </div>
        <div v-if="formData.hsn_code" class="hsn-badge">
          HSN {{ formData.hsn_code }} · GST {{ formData.cgst_rate + formData.sgst_rate }}%
        </div>
      </div>

      <IntercomSelect
        ref="categoryRef"
        v-model="formData.category_id"
        :label="$t('products.category')"
        :options="categorySelectOptions"
        placeholder="No Category"
        filterable
        @update:model-value="handleCategorySelect"
        @change="advance('category')"
      />

      <!-- Inline New Category Form -->
      <div v-if="showNewCategoryForm" class="new-category-form">
        <div class="new-category-header">
          <span class="new-category-title">New Category</span>
        </div>
        <IntercomInput
          v-model="newCategoryName"
          label="Category Name"
          placeholder="e.g. Beverages"
          :error="newCategoryError"
        />
        <div class="new-category-actions">
          <IntercomButton variant="secondary" size="sm" @click="cancelNewCategory">Cancel</IntercomButton>
          <IntercomButton variant="primary" size="sm" :loading="isCreatingCategory" @click="saveNewCategory">Add Category</IntercomButton>
        </div>
      </div>

      <div class="form-row-2">
        <IntercomInput
          ref="stockQuantityRef"
          v-model.number="formData.stock_quantity"
          type="number"
          :label="$t('products.stockQuantity')"
          :placeholder="$t('products.stockQuantity')"
          @keydown.enter.prevent="advance('stockQuantity')"
        />
        <IntercomInput
          ref="minStockQuantityRef"
          v-model.number="formData.min_stock_quantity"
          type="number"
          label="Low Stock Alert"
          placeholder="0 = no alert"
          @keydown.enter.prevent="advance('minStockQuantity')"
        />
      </div>

      <!-- More Options (collapsed by default) -->
      <button type="button" class="more-options-toggle" @click="showMoreOptions = !showMoreOptions">
        <svg :class="{ rotated: showMoreOptions }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6" />
        </svg>
        More options
        <span v-if="moreOptionsCount > 0" class="more-options-badge">{{ moreOptionsCount }}</span>
      </button>

      <div v-if="showMoreOptions" class="more-options-section">
        <!-- Barcode -->
        <div class="barcode-row">
          <IntercomInput
            ref="barcodeRef"
            v-model="formData.barcode"
            :label="$t('products.barcode')"
            placeholder="Scan or type barcode..."
            class="barcode-input"
            @keydown.enter.prevent="lookupBarcode"
          />
          <button type="button" class="scan-btn-sm" @click="openScanner" :title="$t('products.scanBarcode')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 7V5a2 2 0 0 1 2-2h2" />
              <path d="M17 3h2a2 2 0 0 1 2 2v2" />
              <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
              <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
              <line x1="7" y1="12" x2="17" y2="12" />
              <line x1="7" y1="8" x2="13" y2="8" />
              <line x1="7" y1="16" x2="11" y2="16" />
            </svg>
          </button>
        </div>
        <div v-if="barcodeLookupMsg" class="barcode-msg" :class="barcodeLookupType">{{ barcodeLookupMsg }}</div>

        <IntercomInput
          ref="skuRef"
          v-model="formData.sku"
          :label="$t('products.sku')"
          :placeholder="$t('products.sku')"
          required
          :error="errors.sku"
          @keydown.enter.prevent="advance('sku')"
        />

        <IntercomTransliterateInput
          ref="tamilNameRef"
          v-model="formData.tamil_name"
          :label="$t('products.tamilName')"
          placeholder-tamil="Type in English for Tamil..."
          placeholder-english="Type Tamil name directly..."
          default-mode="tamil"
          @keydown.enter.prevent="advance('tamilName')"
        />

        <div class="form-row-2">
          <IntercomSelect
            ref="unitTypeRef"
            v-model="formData.unit_type"
            :label="$t('products.unitType')"
            :options="unitTypeOptions"
            @change="advance('unitType')"
          />
          <div class="form-field">
            <label class="form-checkbox-label">
              <input v-model="formData.is_variable_weight" type="checkbox" class="form-checkbox" @change="handleVariableWeightChange" />
              <span>{{ $t('products.isVariableWeight') }}</span>
            </label>
            <span class="field-hint">{{ $t('products.variableWeightHint') || 'For items sold by weight' }}</span>
          </div>
        </div>

        <!-- Tax Details -->
        <div class="form-field">
          <label class="form-checkbox-label">
            <input v-model="formData.is_taxable" type="checkbox" class="form-checkbox" />
            <span>{{ $t('products.isTaxable') }}</span>
          </label>
        </div>

        <template v-if="formData.is_taxable">
          <IntercomInput
            ref="hsnCodeRef"
            v-model="formData.hsn_code"
            :label="$t('products.hsnCode')"
            :placeholder="$t('products.hsnCode')"
            :required="formData.is_taxable"
            :error="errors.hsn_code"
            @keydown.enter.prevent="advance('hsnCode')"
          />

          <button type="button" class="advanced-toggle" @click="showAdvancedTax = !showAdvancedTax">
            {{ showAdvancedTax ? 'Hide custom GST rates' : 'Custom GST rates' }}
          </button>

          <div v-if="showAdvancedTax" class="tax-rates-grid">
            <IntercomInput ref="cgstRef" v-model.number="formData.cgst_rate" type="number" label="CGST %" placeholder="0.00" @keydown.enter.prevent="advance('cgst')" />
            <IntercomInput ref="sgstRef" v-model.number="formData.sgst_rate" type="number" label="SGST %" placeholder="0.00" @keydown.enter.prevent="advance('sgst')" />
            <IntercomInput ref="igstRef" v-model.number="formData.igst_rate" type="number" label="IGST %" placeholder="0.00" @keydown.enter.prevent="advance('igst')" />
          </div>
        </template>
      </div>
    </div>

    <template #footer>
      <IntercomButton variant="secondary" @click="handleCancel">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton variant="primary" :loading="isLoading" @click="handleSubmit">
        {{ $t('common.save') }}
      </IntercomButton>
    </template>
  </IntercomModal>

  <!-- Camera Scanner Modal -->
  <Teleport to="body">
    <CameraScanner
      v-if="showScanner"
      :onScan="handleBarcodeScan"
      @close="closeScanner"
      @scan="handleBarcodeScan"
    />
  </Teleport>
</template>

<script setup>
import { ref, reactive, watch, onMounted, computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { IntercomModal, IntercomInput, IntercomButton, IntercomSelect, IntercomTransliterateInput } from '@shared/components/intercom'
import { useFormKeyboard } from '@shared/composables/useFormKeyboard.js'
import { useBilling } from '../composables/useBilling.js'
import { buildUnitOptions } from '../constants/unitTypes.js'
import CameraScanner from './CameraScanner.vue'

const props = defineProps({
  show: Boolean,
  product: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['update:show', 'success'])

const { t } = useI18n()
const { createProduct, updateProduct, listCategories, createCategory, getProductByBarcode, searchHSN, getHSNByCode, getBillingSettings } = useBilling()

// State
const showModal = ref(props.show)
const isLoading = ref(false)
const categories = ref([])
const categoryOptions = ref([])
const categorySelectOptions = computed(() => [
  { value: null, label: 'No Category' },
  ...categoryOptions.value,
  { value: '__create_new__', label: '+ New Category', isCreateNew: true },
])
const showScanner = ref(false)
const showAdvancedTax = ref(false)
const showMoreOptions = ref(false)

// Keyboard navigation refs
const nameRef = ref(null)
const basePriceRef = ref(null)
const hsnSearchRef = ref(null)
const categoryRef = ref(null)
const stockQuantityRef = ref(null)
const minStockQuantityRef = ref(null)
const barcodeRef = ref(null)
const skuRef = ref(null)
const tamilNameRef = ref(null)
const unitTypeRef = ref(null)
const hsnCodeRef = ref(null)
const cgstRef = ref(null)
const sgstRef = ref(null)
const igstRef = ref(null)

// Keyboard navigation composable
const { field: kbField, advance, handleEscape, clearFields, focusFirst } = useFormKeyboard({
  onSubmit: () => handleSubmit(),
  onEscape: () => { showModal.value = false },
})

/**
 * Register fields in tab order for keyboard navigation.
 * Conditionally hidden fields (More Options, Advanced Tax) are registered
 * but skipped automatically when their ref is null.
 */
const registerKeyboardFields = () => {
  clearFields()
  // Essential fields (always visible)
  kbField('name', nameRef)
  kbField('basePrice', basePriceRef, { type: 'number' })
  kbField('hsnSearch', hsnSearchRef)
  kbField('category', categoryRef, { type: 'select' })
  kbField('stockQuantity', stockQuantityRef, { type: 'number' })
  kbField('minStockQuantity', minStockQuantityRef, { type: 'number' })
  // More Options fields (conditionally visible)
  kbField('barcode', barcodeRef)
  kbField('sku', skuRef)
  kbField('tamilName', tamilNameRef)
  kbField('unitType', unitTypeRef, { type: 'select' })
  // Tax fields (conditionally visible)
  kbField('hsnCode', hsnCodeRef)
  kbField('cgst', cgstRef, { type: 'number' })
  kbField('sgst', sgstRef, { type: 'number' })
  kbField('igst', igstRef, { type: 'number' })
}

// HSN product type search
const isSearchingHSN = ref(false)
const hsnOptions = ref([])
const hsnSearchQuery = ref('')
const hsnDropdownOpen = ref(false)
const billingSettings = ref({})
let hsnSearchTimer = null

const handleHSNSearch = () => {
  if (hsnSearchTimer) clearTimeout(hsnSearchTimer)
  const q = hsnSearchQuery.value?.trim()
  if (!q || q.length < 2) {
    hsnOptions.value = []
    hsnDropdownOpen.value = false
    return
  }
  isSearchingHSN.value = true
  hsnDropdownOpen.value = true
  hsnSearchTimer = setTimeout(async () => {
    try {
      const bizType = billingSettings.value?.business_type || ''
      const res = await searchHSN(q, 15, bizType)
      const results = res.results || res.data?.results || []
      hsnOptions.value = results.map((h) => ({
        label: h.label,
        value: h.id,
        hsn: h.hsn,
        gst: h.gst_rate,
      }))
      hsnDropdownOpen.value = hsnOptions.value.length > 0
    } catch {
      hsnOptions.value = []
      hsnDropdownOpen.value = false
    } finally {
      isSearchingHSN.value = false
    }
  }, 300)
}

// Show recently used HSN codes on focus (empty query)
const handleHSNFocus = async () => {
  if (hsnSearchQuery.value?.trim()) {
    hsnDropdownOpen.value = hsnOptions.value.length > 0
    return
  }
  // Fetch recently used / popular for business type
  try {
    isSearchingHSN.value = true
    hsnDropdownOpen.value = true
    const bizType = billingSettings.value?.business_type || ''
    const res = await searchHSN('', 10, bizType)
    const results = res.results || res.data?.results || []
    hsnOptions.value = results.map((h) => ({
      label: h.label,
      value: h.id,
      hsn: h.hsn,
      gst: h.gst_rate,
    }))
    hsnDropdownOpen.value = hsnOptions.value.length > 0
  } catch {
    // Silently fail — no recently used codes available
  } finally {
    isSearchingHSN.value = false
  }
}

const selectHSNOption = (option) => {
  hsnSearchQuery.value = option.label
  hsnDropdownOpen.value = false
  formData.hsn_id = option.value
  formData.hsn_code = option.hsn

  const rate = option.gst
  const slabKey = String(rate)
  if (GST_SLABS[slabKey]) {
    formData.gst_slab = slabKey
    applyGstSlab(slabKey)
  } else {
    const half = rate / 2
    formData.cgst_rate = half
    formData.sgst_rate = half
    formData.igst_rate = rate
    formData.gst_slab = null
  }
}

const clearHSNSelection = () => {
  hsnSearchQuery.value = ''
  hsnOptions.value = []
  hsnDropdownOpen.value = false
  formData.hsn_id = null
  formData.hsn_code = ''
  formData.gst_slab = '18'
  applyGstSlab('18')
}

const closeHSNDropdown = () => {
  setTimeout(() => { hsnDropdownOpen.value = false }, 150)
}

/** Load HSN entry by code — used in edit mode to restore the Product Type label */
const loadHSNByCode = async (code) => {
  try {
    const res = await getHSNByCode(code)
    const results = res.results || res.data?.results || []
    if (results.length > 0) {
      // Pick the entry matching the product's GST rate, or first
      const match = results.find((h) => h.gst_rate === formData.cgst_rate + formData.sgst_rate) || results[0]
      hsnSearchQuery.value = match.label
      formData.hsn_id = match.id
    } else {
      // Code not in catalog — show the raw code
      hsnSearchQuery.value = `HSN ${code}`
    }
  } catch {
    hsnSearchQuery.value = `HSN ${code}`
  }
}

// Badge count for "More options" — shows how many optional fields have values
const moreOptionsCount = computed(() => {
  let count = 0
  if (formData.barcode) count++
  if (formData.tamil_name) count++
  if (formData.is_variable_weight) count++
  if (formData.hsn_code) count++
  return count
})

// Barcode lookup state
const barcodeLookupMsg = ref('')
const barcodeLookupType = ref('') // 'found' or 'new'

// Inline new category state
const showNewCategoryForm = ref(false)
const isCreatingCategory = ref(false)
const newCategoryName = ref('')
const newCategoryError = ref('')

// GST 2.0 slab → CGST/SGST/IGST lookup table (effective 22 Sep 2025)
// 12% and 28% slabs eliminated. Two-tier: 5% and 18%. 40% for sin goods.
const GST_SLABS = {
  '0':  { cgst: 0,    sgst: 0,    igst: 0  },
  '5':  { cgst: 2.5,  sgst: 2.5,  igst: 5  },
  '18': { cgst: 9,    sgst: 9,    igst: 18 },
  '40': { cgst: 20,   sgst: 20,   igst: 40 },
}

// Unit type options — populated from billing settings (enabled UQC codes)
const unitTypeOptions = ref([])

const gstSlabOptions = [
  { value: '0', label: 'Exempt (0%)' },
  { value: '5', label: '5%' },
  { value: '18', label: '18% (Standard)' },
  { value: '40', label: '40% (Sin goods)' },
]

/**
 * Reverse-lookup: given a cgst_rate, find the matching slab key.
 * Falls back to '18' (most common) if no match is found.
 */
const slabFromCgst = (cgstRate) => {
  for (const [key, val] of Object.entries(GST_SLABS)) {
    if (val.cgst === cgstRate) return key
  }
  return '18'
}

// Form data
const formData = reactive({
  sku: '',
  name: '',
  tamil_name: '',
  category_id: null,
  base_price: 0,
  is_taxable: true,
  hsn_code: '',
  hsn_id: null, // Selected HSN catalog entry ID
  stock_quantity: 0,
  min_stock_quantity: 0,
  barcode: '',
  unit_type: 'PCS',
  is_variable_weight: false,
  gst_slab: '18',
  cgst_rate: 9,
  sgst_rate: 9,
  igst_rate: 18,
})

// Form errors
const errors = reactive({
  sku: '',
  name: '',
  base_price: '',
  hsn_code: '',
})

// Re-register keyboard fields when conditionally visible sections change
watch([showMoreOptions, showAdvancedTax, () => formData.is_taxable], () => {
  nextTick(registerKeyboardFields)
})

// Watch show prop
watch(
  () => props.show,
  (val) => {
    showModal.value = val
    if (val && props.product) {
      // Edit mode - populate form
      const cgst = props.product.cgst_rate ?? 9
      const sgst = props.product.sgst_rate ?? 9
      const igst = props.product.igst_rate ?? 18
      Object.assign(formData, {
        sku: props.product.sku,
        name: props.product.name,
        tamil_name: props.product.localizations?.ta_IN || '',
        category_id: props.product.category_id || null,
        base_price: props.product.base_price,
        is_taxable: props.product.is_taxable,
        hsn_code: props.product.hsn_code || '',
        stock_quantity: props.product.stock_quantity,
        min_stock_quantity: props.product.min_stock_quantity || 0,
        barcode: props.product.barcode || '',
        unit_type: props.product.unit_type || 'PCS',
        is_variable_weight: props.product.is_variable_weight || false,
        cgst_rate: cgst,
        sgst_rate: sgst,
        igst_rate: igst,
        gst_slab: slabFromCgst(cgst),
      })
      showAdvancedTax.value = false
      // Show "More Options" if product has optional fields filled
      showMoreOptions.value = !!(props.product.barcode || props.product.localizations?.ta_IN || props.product.is_variable_weight || props.product.hsn_code)
      // Restore Product Type from HSN code
      if (props.product.hsn_code) {
        loadHSNByCode(props.product.hsn_code)
      } else {
        hsnSearchQuery.value = ''
      }
    } else if (val) {
      // Create mode - reset form and re-fetch categories (picks up newly created ones)
      resetForm()
      fetchCategories()
    }
    // Clear errors when opening
    clearErrors()
    // Auto-focus the product name field when modal opens
    if (val) {
      nextTick(() => {
        registerKeyboardFields()
        nameRef.value?.focus?.()
        // Fallback for IntercomInput: find inner input
        if (nameRef.value?.$el) {
          nameRef.value.$el.querySelector?.('input')?.focus()
        }
      })
    }
  }
)

// Watch modal state — focus first field when modal opens
watch(showModal, (val) => {
  emit('update:show', val)
  if (val) {
    nextTick(() => focusFirst())
  }
})

/**
 * Open barcode scanner
 */
const openScanner = () => {
  showScanner.value = true
}

/**
 * Close barcode scanner
 */
const closeScanner = () => {
  showScanner.value = false
}

/**
 * Lookup barcode — if product exists, fill form for editing; if not, keep barcode for new product
 */
const lookupBarcode = async () => {
  const barcode = formData.barcode?.trim()
  if (!barcode) return

  barcodeLookupMsg.value = ''
  barcodeLookupType.value = ''

  try {
    const product = await getProductByBarcode(barcode)
    if (product && product.id) {
      // Product found — fill form with its data
      const cgst = product.cgst_rate ?? 9
      const sgst = product.sgst_rate ?? 9
      const igst = product.igst_rate ?? 18
      Object.assign(formData, {
        sku: product.sku,
        name: product.name,
        tamil_name: product.localizations?.ta_IN || '',
        category_id: product.category_id || null,
        base_price: product.base_price,
        is_taxable: product.is_taxable,
        hsn_code: product.hsn_code || '',
        stock_quantity: product.stock_quantity,
        min_stock_quantity: product.min_stock_quantity || 0,
        barcode: product.barcode || barcode,
        unit_type: product.unit_type || 'PCS',
        is_variable_weight: product.is_variable_weight || false,
        cgst_rate: cgst,
        sgst_rate: sgst,
        igst_rate: igst,
        gst_slab: slabFromCgst(cgst),
      })
      barcodeLookupMsg.value = `Found: ${product.name} — form filled`
      barcodeLookupType.value = 'found'
    }
  } catch {
    // Product not found — keep barcode, let user fill the rest
    barcodeLookupMsg.value = 'New barcode — fill in product details'
    barcodeLookupType.value = 'new'
  }
}

/**
 * Handle barcode scan from camera — set barcode and auto-lookup
 */
const handleBarcodeScan = (barcode) => {
  formData.barcode = barcode
  closeScanner()
  lookupBarcode()
}

/**
 * Apply the selected GST slab to the CGST/SGST/IGST fields
 */
const applyGstSlab = (value) => {
  const slab = GST_SLABS[value || formData.gst_slab]
  if (slab) {
    formData.cgst_rate = slab.cgst
    formData.sgst_rate = slab.sgst
    formData.igst_rate = slab.igst
  }
}

/**
 * Handle variable weight change
 */
const handleVariableWeightChange = () => {
  if (formData.is_variable_weight) {
    // Auto-select KGS for variable weight items
    formData.unit_type = 'KGS'
  }
}

/**
 * Clear errors
 */
const clearErrors = () => {
  errors.sku = ''
  errors.name = ''
  errors.base_price = ''
  errors.hsn_code = ''
}

/**
 * Validate form
 */
const validateForm = () => {
  clearErrors()
  let isValid = true

  // Validate SKU
  if (!formData.sku || formData.sku.trim() === '') {
    errors.sku = 'SKU is required'
    isValid = false
  }

  // Validate name
  if (!formData.name || formData.name.trim() === '') {
    errors.name = 'Product name is required'
    isValid = false
  }

  // Validate base price
  if (!formData.base_price || formData.base_price <= 0) {
    errors.base_price = 'Base price is required and must be greater than 0'
    isValid = false
  }

  // Validate HSN code if taxable
  if (formData.is_taxable && (!formData.hsn_code || formData.hsn_code.trim() === '')) {
    errors.hsn_code = 'HSN code is required for taxable products'
    isValid = false
  }

  return isValid
}

/**
 * Generate a short unique SKU like SKU-A3X9KP
 */
const generateSKU = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let id = ''
  for (let i = 0; i < 6; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return `SKU-${id}`
}

/**
 * Reset form
 */
const resetForm = () => {
  // Use the first enabled unit as default, or fall back to 'PCS'
  const defaultUnit = unitTypeOptions.value.length > 0 ? unitTypeOptions.value[0].value : 'PCS'
  Object.assign(formData, {
    sku: generateSKU(),
    name: '',
    tamil_name: '',
    category_id: null,
    base_price: 0,
    is_taxable: true,
    hsn_code: '',
    hsn_id: null,
    stock_quantity: 0,
    min_stock_quantity: 0,
    barcode: '',
    unit_type: defaultUnit,
    is_variable_weight: false,
    gst_slab: '18',
    cgst_rate: 9,
    sgst_rate: 9,
    igst_rate: 18,
  })
  showAdvancedTax.value = false
  showMoreOptions.value = false
  hsnSearchQuery.value = ''
  hsnOptions.value = []
  hsnDropdownOpen.value = false
  barcodeLookupMsg.value = ''
  barcodeLookupType.value = ''
  clearErrors()
}

/**
 * Handle cancel
 */
const handleCancel = () => {
  showModal.value = false
  resetForm()
}

/**
 * Handle submit
 */
const handleSubmit = async () => {
  // Validate form
  if (!validateForm()) {
    return
  }

  isLoading.value = true

  try {
    // Prepare data
    const productData = {
      sku: formData.sku,
      name: formData.name,
      category_id: formData.category_id,
      base_price: formData.base_price,
      is_taxable: formData.is_taxable,
      hsn_code: formData.is_taxable ? formData.hsn_code : null,
      hsn_id: formData.hsn_id || null,
      cgst_rate: formData.is_taxable ? formData.cgst_rate : 0,
      sgst_rate: formData.is_taxable ? formData.sgst_rate : 0,
      igst_rate: formData.is_taxable ? formData.igst_rate : 0,
      stock_quantity: formData.stock_quantity,
      min_stock_quantity: formData.min_stock_quantity || 0,
      barcode: formData.barcode || null,
      unit_type: formData.unit_type,
      is_variable_weight: formData.is_variable_weight,
      localizations: formData.tamil_name
        ? {
            ta_IN: formData.tamil_name,
          }
        : null,
    }

    // Create or update
    if (props.product) {
      await updateProduct(props.product.id, productData)
      console.log(t('products.updateSuccess'))
    } else {
      await createProduct(productData)
      console.log(t('products.createSuccess'))
    }

    // Close modal and emit success
    showModal.value = false
    resetForm()
    emit('success')
  } catch (error) {
    console.error('Failed to save product:', error)
    // TODO: Show error notification
  } finally {
    isLoading.value = false
  }
}

/**
 * Handle category select — intercept the __create_new__ sentinel
 */
const handleCategorySelect = (value) => {
  if (value === '__create_new__') {
    showNewCategoryForm.value = true
    formData.category_id = null
    return
  }
}

/**
 * Save the inline new category
 */
const saveNewCategory = async () => {
  newCategoryError.value = ''
  if (!newCategoryName.value.trim()) {
    newCategoryError.value = 'Category name is required'
    return
  }
  isCreatingCategory.value = true
  try {
    const response = await createCategory({ name: newCategoryName.value.trim() })
    const created = response.category || response
    categories.value.push(created)
    categoryOptions.value.push({ label: created.name, value: created.id })
    formData.category_id = created.id
    showNewCategoryForm.value = false
    newCategoryName.value = ''
  } catch (err) {
    newCategoryError.value = 'Failed to create category'
  } finally {
    isCreatingCategory.value = false
  }
}

/**
 * Cancel inline category creation
 */
const cancelNewCategory = () => {
  showNewCategoryForm.value = false
  newCategoryName.value = ''
  newCategoryError.value = ''
}

/**
 * Fetch categories for dropdown.
 * In create mode, auto-select the most recently created category
 * so that after creating a category, the next "Add Product" picks it up.
 */
const fetchCategories = async () => {
  try {
    const response = await listCategories({ limit: 100 })
    categories.value = response.data || []

    // Transform to select options
    categoryOptions.value = categories.value.map((cat) => ({
      label: cat.name,
      value: cat.id,
    }))

  } catch (error) {
    console.error('Failed to fetch categories:', error)
  }
}

/**
 * Fetch enabled unit codes from billing settings and build dropdown options.
 * Falls back to a minimal set of common units if the request fails.
 */
const fetchUnitSettings = async () => {
  try {
    const settings = await getBillingSettings()
    const enabledCodes = settings?.enabled_unit_codes
    if (enabledCodes && enabledCodes.length > 0) {
      unitTypeOptions.value = buildUnitOptions(enabledCodes)
    } else {
      // No explicit setting — use defaults
      unitTypeOptions.value = buildUnitOptions(['PCS', 'KGS', 'GMS', 'LTR', 'MLT', 'MTR', 'BOX', 'PAC', 'SET', 'NOS'])
    }
    // Apply default unit from settings if form hasn't been touched yet
    if (settings?.default_unit_code && formData.unit_type === 'PCS') {
      formData.unit_type = settings.default_unit_code
    }
  } catch {
    // Fallback to common units so the form remains usable
    unitTypeOptions.value = buildUnitOptions(['PCS', 'KGS', 'GMS', 'LTR', 'MLT', 'MTR', 'BOX', 'PAC', 'SET', 'NOS', 'DOZ', 'OTH'])
  }
}

// Load categories and unit settings on mount
onMounted(async () => {
  fetchCategories()
  fetchUnitSettings()
  registerKeyboardFields()
  // Fetch billing settings for business_type (HSN priority boosting)
  try {
    const s = await getBillingSettings()
    billingSettings.value = s?.data || s || {}
  } catch { /* defaults apply */ }
})
</script>

<style scoped>
.product-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

/* Product Type (HSN) field */
.product-type-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.field-label-sm {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.hsn-search-wrapper {
  position: relative;
}

.hsn-search-input {
  width: 100%;
  height: 44px;
  padding: 0 var(--space-8) 0 var(--space-3);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  outline: none;
  transition: all var(--transition-fast);
}

.hsn-search-input:focus {
  background: var(--color-white);
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1);
}

.hsn-clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: var(--color-gray-400);
  background: var(--color-gray-200);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  line-height: 1;
}

.hsn-clear-btn:hover {
  background: var(--color-gray-300);
  color: var(--color-charcoal);
}

.hsn-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg, 0 4px 12px rgba(0,0,0,0.1));
  max-height: 240px;
  overflow-y: auto;
  z-index: 100;
}

.hsn-dropdown-loading {
  padding: var(--space-3);
  text-align: center;
  font-size: var(--font-size-sm);
  color: var(--color-gray-400);
}

.hsn-dropdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.hsn-dropdown-item:hover {
  background: var(--color-gray-50);
}

.hsn-item-label {
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
}

.hsn-item-meta {
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
  white-space: nowrap;
  margin-left: var(--space-2);
}

.hsn-badge {
  font-size: var(--font-size-xs);
  color: var(--color-green, #16a34a);
  font-weight: var(--font-weight-medium);
}

/* Two-column row */
.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

/* More Options toggle */
.more-options-toggle {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  background: none;
  border: none;
  cursor: pointer;
  transition: color var(--transition-fast);
}

.more-options-toggle:hover {
  color: var(--color-charcoal);
}

.more-options-toggle svg {
  transition: transform var(--transition-fast);
}

.more-options-toggle svg.rotated {
  transform: rotate(90deg);
}

.more-options-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: white;
  background: var(--color-blue);
  border-radius: 9px;
}

/* More Options section */
.more-options-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-4);
  background: var(--color-gray-50);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-gray-200);
}

/* Barcode row */
.barcode-row {
  display: flex;
  gap: var(--space-2);
  align-items: flex-end;
}

.barcode-row .barcode-input {
  flex: 1;
}

.scan-btn-sm {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--color-gray-100);
  color: var(--color-gray-600);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.scan-btn-sm:hover {
  background: var(--color-blue);
  color: white;
  border-color: var(--color-blue);
}

.barcode-msg {
  font-size: var(--font-size-xs);
  margin-top: calc(var(--space-2) * -1);
}

.barcode-msg.found {
  color: var(--color-green, #16a34a);
}

.barcode-msg.new {
  color: var(--color-blue);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.form-checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  cursor: pointer;
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
}

.form-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--color-blue);
}

.field-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
}

/* Inline New Category Form */
.new-category-form {
  background: var(--color-blue-50, #eff6ff);
  border: 1px solid var(--color-blue-200, #bfdbfe);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-top: calc(var(--space-2) * -1);
}

.new-category-header {
  display: flex;
  align-items: center;
}

.new-category-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-blue, #3b82f6);
}

.new-category-actions {
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
}

/* GST Slab toggle link below the dropdown */
.advanced-toggle {
  display: inline-block;
  margin-top: var(--space-1);
  padding: 0;
  font-size: var(--font-size-xs);
  color: var(--color-blue);
  background: none;
  border: none;
  outline: none;
  box-shadow: none;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  width: auto;
}

.advanced-toggle:hover {
  opacity: 0.75;
}

.advanced-toggle:focus,
.advanced-toggle:focus-visible {
  outline: none;
  box-shadow: none;
}

/* Advanced manual rate fields — no box, compact */
.advanced-tax-fields {
  padding-top: var(--space-2);
}

.tax-rates-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-2);
}
</style>
